import {ReactElement} from 'react';
import {I18n} from 'aws-amplify/utils';
import {Box} from '@mantine/core';
import {
    ReloadButton,
    SimplePageHeader,
} from '@flexinet/ui-components';

import {useZones} from '../hooks/useZones';
import {useZonesSelection} from '../hooks/useZonesSelection';
import {useZoneGroupDeleteTarget} from '../hooks/useZoneGroupDeleteTarget';
import {useZoneDeletion} from '../hooks/useZoneDeletion';
import {FilterRecordsButton} from '../buttons/FilterRecordsButton';
import {AddRecordButton} from '../buttons/AddRecordButton';
import {DeleteRecordButton} from '../buttons/DeleteRecordButton';
import {DeleteZoneModal} from './DeleteZoneModal';
import {showErrorNotification} from '../../../components/ServiceNotifications';
import config from '../config';

const ZonesReloadButton = () => {
    const {reload} = useZones();
    return (
        <Box mx={'s'}>
            <ReloadButton onClick={() => reload()}/>
        </Box>
    );
};

// FNP-1191 CRUD half — the delete affordance, gated per selected zone.
//
// ⚠️ THE CAPABILITY IS RESOLVED FROM THE SELECTED ZONE'S OWN CONNECTION, not
// from "the tenant" — there is no such thing as a tenant-wide write capability
// here. Zones fan out across every connection, and a tenant can hold a writable
// Route 53 connection and a read-only Cloudflare one at the same time. Asking
// "can I delete THIS zone" is the only question with an answer.
//
// ⚠️ FNP-1446 — RESOLVED ACROSS EVERY CONNECTION THAT REACHES THE ZONE. A
// grouped row stands for one real zone read through several connections, so
// "this zone's own connection" is not a single thing. The question is whether
// ANY of them may delete it; `useZoneGroupDeleteTarget` answers that and hands
// back the row to delete THROUGH, so the check and the mutation cannot end up
// on different credentials.
//
// ⚠️ AND THE DEFAULT SURVIVES. Every unknown still counts as not permitted --
// loading, errored, no snapshot, connection not ACTIVE. Adding connections to
// the question did not soften it: a zone is deletable only once some
// connection has actually answered yes.
const DeleteZoneTool = () => {

    const {selectedRecords} = useZonesSelection();
    const {items: groups} = useZones();

    // One selected zone is the only state with an unambiguous connection. Zero
    // renders a disabled button; more than one is refused below.
    const selected = selectedRecords.length === 1 ? selectedRecords[0] : undefined;

    // ⚠️ FNP-1446 — EVERY CONNECTION THAT REACHES THE ZONE, NOT JUST THE ONE
    // SELECTION HAPPENED TO STORE.
    //
    // Selection stores a group's `representative`, which FNP-1378 defines as
    // the first row in fetch order. Asking only that row "can you delete?"
    // hides the button on a zone another connection can delete perfectly well,
    // and the operator sees nothing to explain it. The group's other rows are
    // recovered here so the question can be put to all of them.
    //
    // Falls back to the selected row alone if its group cannot be found — a
    // zone loaded but not grouped is not a state this should crash on, and one
    // connection is still a correct, if narrower, answer.
    const selectedGroup = selected
        ? groups.find(group => group.representative.id === selected.id)
        : undefined;

    const {
        deleteTarget,
        canDeleteZones,
    } = useZoneGroupDeleteTarget(
        selected
            ? (selectedGroup?.zones ?? [selected])
            : [],
    );

    const {
        zone,
        opened,
        requestDelete,
        cancelDelete,
        confirmDelete,
        isDeleting,
        error,
    } = useZoneDeletion();

    // ⚠️ HIDDEN, NOT DISABLED, when the credential cannot delete. A disabled
    // button promises a capability that does not exist; an absent one is a
    // correct statement about a read-only connection.
    //
    // ⚠️ FNP-1457 — ONLY A CREDENTIAL REFUSAL HIDES THIS. A selection whose
    // SIZE makes the question unanswerable is a different fact and must not be
    // read as a no.
    //
    // `canDeleteZones` is false in both cases and that is correct in both: with
    // zero or several rows ticked, `selected` is undefined, the group above
    // resolves to `[]`, and "no connection may delete nothing" is a true answer
    // to a question nobody asked. Hiding on it conflated the two, and the
    // damage was not merely a missing button — `handleDelete`'s multi-row
    // refusal below became UNREACHABLE, because the control that calls it had
    // already returned null. The owner ticked two zones, watched Delete vanish,
    // and got no explanation, because the sentence written to explain it could
    // never run.
    //
    // So the hide is now asked only where an unambiguous question exists: one
    // row selected, and no connection reaching it answered yes. Zero and
    // several both keep the button, which is also what the toolbar wants — a
    // control that appears and disappears as rows are ticked is harder to find
    // than one that greys out or explains itself.
    const ambiguousSelection = selectedRecords.length !== 1;
    if (!ambiguousSelection && !canDeleteZones) {
        return null;
    }

    const handleDelete = () => {
        // ⚠️ REFUSES A MULTI-ROW DELETE rather than fanning out. Every zone
        // carries its own refusal and its own delegation verdict, and a batch
        // reports one outcome for several — see hooks/useZoneDeletion.
        if (selectedRecords.length > 1) {
            showErrorNotification(
                I18n.get('Delete one zone at a time. Each zone has its own delegation state and its own provider refusal, and a batch would report one outcome for all of them.'),
            );
            return;
        }
        // ⚠️ THE ROW THAT PASSED THE CHECK IS THE ROW THE DELETE GOES THROUGH.
        // Passing `selected` here would send the mutation down the
        // representative's connection while the button was enabled on a
        // different one's grant — the check and the action disagreeing, which
        // is the same defect one layer down.
        if (deleteTarget) {
            requestDelete(deleteTarget);
        }
    };

    return (
        <>
            <DeleteRecordButton
                count={selectedRecords.length}
                onClick={handleDelete}
            />
            <DeleteZoneModal
                zone={zone}
                opened={opened}
                onClose={cancelDelete}
                onConfirm={confirmDelete}
                isDeleting={isDeleting}
                error={error}
            />
        </>
    );
};

const useTools = (): ReactElement[] => {
    // FNP-1191 CRUD half: Add and Delete join Filter and Reload.
    //
    // Both write controls hide themselves on capability rather than being
    // conditionally rendered here — the decision belongs with the control that
    // knows which connection it is asking about.
    return [
        <AddRecordButton key={'add'}/>,
        <DeleteZoneTool key={'delete'}/>,
        <FilterRecordsButton key={'filter'}/>,
        <ZonesReloadButton key={'reload'}/>,
    ];
};

export const IndexPageHeader = () => {
    return (
        <SimplePageHeader
            title={I18n.get(config.titles.plural)}
            tools={useTools()}
        />
    );
};
